import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FileText, List, Pencil } from "lucide-react";
import { CardContent } from "@/components/ui/card";
import ViewLayout from "@/v2/layout/View";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import AccessControl from "@/v2/components/rbac/AccessControl";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import TermsAndConditionDeleteButton from "@/v2/components/ui/button/delete/TermsAndCondition";
import FindOneTermsAndCondition from "@/v2/api/terms-and-conditions/FindOne";

const NavButtons = ({ id }: { id: string }) => (
    <>
        <AccessControl module="SUPPLIER_MANAGEMENT" requestedAccess={"PUT"}>
            <Link to={`/templates/terms-and-conditions/edit/${id}`}>
                <Button variant={"outline"} className="p-4" size={"sm"}>
                    <Pencil className="h-4 w-4" />
                    {"Edit"}
                </Button>
            </Link>
        </AccessControl>

        <TermsAndConditionDeleteButton id={id} isInside />
    </>
);

export default function TermsAndConditionViewOne() {
    const { id } = useParams();
    const navigate = useNavigate();

    if (!id) navigate(-1);

    const query = useQuery({
        queryKey: ["terms", id],
        queryFn: () => FindOneTermsAndCondition(id!),
    });

    if (query.isLoading || query.isFetching) return <LoadingAnimation />;
    if (!query.data) return <p>Something went wrong</p>;

    const terms = query.data;

    return (
        <ViewLayout
            title="Terms & Conditions"
            description="Template details and clauses"
            icon={FileText}
            buttons={<NavButtons id={id!} />}
        >
            <CardContent className="space-y-6">
                {/* Header Section */}
                <div>
                    <Label className="text-sm text-muted-foreground">
                        Template Name
                    </Label>
                    <h2 className="text-xl font-semibold">
                        {terms?.template_name}
                    </h2>
                </div>

                <Separator />

                {/* Terms and Conditions */}
                <div className="space-y-3">
                    <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <List className="h-4 w-4" />
                        Terms & Conditions
                    </Label>

                    {terms?.terms_and_conditions?.length ? (
                        <ul className="list-decimal space-y-2 pl-6 text-base font-medium">
                            {terms.terms_and_conditions.map((item, index) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            No terms added yet.
                        </p>
                    )}
                </div>
            </CardContent>
        </ViewLayout>
    );
}
